import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, inject } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { PartDetailsOverview } from '../../../../models/part-details-overview.model';
import { PartsAndInventoryService } from '../../../../services/parts-and-inventory.service';
import { ErrorHandlerService } from '../../../../../../../_shared/services/error-handler.service';
import { CurrencyHelper } from '../../../../../../../_shared/helpers/currency/currency-map.helper';
import { AuthService } from '../../../../../../../_shared/services';
import { EditPartComponent } from '../../edit-part/edit-part.component';

@Component({
  selector: 'app-part-details-tab',
  standalone: true,
  imports: [CommonModule, TranslocoDirective],
  templateUrl: './part-details-tab.component.html',
  styleUrl: './part-details-tab.component.scss'
})
export class PartDetailsTabComponent implements OnInit, OnDestroy {
  @Input() partId!: number;
  @Output() detailsChanged = new EventEmitter<void>();

  details: PartDetailsOverview | null = null;
  isLoading = false;
  hasError = false;
  isSavingReorderNotification = false;

  private saveTimeout: ReturnType<typeof setTimeout> | null = null;

  private readonly modalService = inject(NgbModal);
  private readonly partsAndInventoryService = inject(PartsAndInventoryService);
  private readonly errorHandlerService = inject(ErrorHandlerService);
  private readonly authService = inject(AuthService);
  private readonly toastr = inject(ToastrService);
  private readonly transloco = inject(TranslocoService);

  ngOnInit(): void {
    void this.loadDetailsAsync();
  }

  ngOnDestroy(): void {
    if (this.saveTimeout) {
      clearTimeout(this.saveTimeout);
    }
  }

  get currentStock(): string {
    return this.details?.currentStock !== null ? String(this.details?.currentStock) : '-';
  }

  get category(): string {
    return this.details?.category || '-';
  }

  get warranty(): string {
    const months = Number(this.details?.warranty);
    if (!Number.isFinite(months) || months <= 0) return '-';
    if (months % 12 === 0) {
      const years = months / 12;
      return this.transloco.translate(
        years === 1 ? 'parts.modals.partDetails.warrantyYear' : 'parts.modals.partDetails.warrantyYears',
        { count: years }
      );
    }
    return this.transloco.translate(
      months === 1 ? 'parts.modals.partDetails.warrantyMonth' : 'parts.modals.partDetails.warrantyMonths',
      { count: months }
    );
  }

  get storageLocation(): string {
    return this.details?.storageLocation || '-';
  }

  get currencySymbol(): string {
    const currencyCode = this.authService.getUserSettings()?.currencyCode ?? 'ZAR';
    return CurrencyHelper.getCurrencySymbol(currencyCode);
  }

  get unitCost(): string {
    return this.details?.unitCost !== null
      ? `${this.currencySymbol}${this.details?.unitCost.toFixed(2)}`
      : '-';
  }

  get reorderPoint(): string {
    return this.details?.reorderPoint !== null ? String(this.details?.reorderPoint) : '-';
  }

  get reorderQuantity(): string {
    return this.details?.reorderQuantity !== null ? String(this.details?.reorderQuantity) : '-';
  }

  get maximumQuantity(): string {
    return this.details?.maximumQuantity !== null ? String(this.details?.maximumQuantity) : '-';
  }

  get isReorderNotificationEnabled(): boolean {
    return this.details?.reorderPointNotification ?? false;
  }

  toggleReorderNotification(): void {
    if (!this.details || this.isSavingReorderNotification) {
      return;
    }

    this.details = {
      ...this.details,
      reorderPointNotification: !this.details.reorderPointNotification
    };

    this.onReorderNotificationChanged();
  }

  openEditPart(): void {
    const modalRef = this.modalService.open(EditPartComponent, {
      size: 'md',
      centered: true,
      backdrop: 'static',
      windowClass: 'edit-part-modal-window'
    });

    modalRef.componentInstance.partId = this.partId;

    modalRef.result
      .then((result: boolean) => {
        if (result === true) {
          this.detailsChanged.emit();
          void this.loadDetailsAsync();
        }
      })
      .catch(() => { });
  }

  private onReorderNotificationChanged(): void {
    if (!this.details) {
      return;
    }

    if (this.saveTimeout) {
      clearTimeout(this.saveTimeout);
    }

    this.saveTimeout = setTimeout(async () => {
      if (!this.details) {
        return;
      }

      this.isSavingReorderNotification = true;

      try {
        await this.partsAndInventoryService.updateReorderPointNotificationAsync({
          partId: this.partId,
          reorderPointNotification: this.details.reorderPointNotification
        });

        this.toastr.success(this.transloco.translate('parts.modals.partDetails.toastSaved'));
      } catch (error: any) {
        this.toastr.error(this.transloco.translate('parts.modals.partDetails.toastSaveFailed'));
        await this.loadDetailsAsync();
      } finally {
        this.isSavingReorderNotification = false;
      }
    }, 1000);
  }

  private async loadDetailsAsync(): Promise<void> {
    this.isLoading = true;
    this.hasError = false;

    try {
      const response = await this.partsAndInventoryService.getPartDetailsOverviewAsync(this.partId);
      this.details = response.data ?? null;
    } catch (error: any) {
      this.hasError = true;
      this.errorHandlerService.handleError(error);
    } finally {
      this.isLoading = false;
    }
  }
}